import { useGetState, useMemoizedFn } from "ahooks";
import classNames from "classnames";
import dayjs from "dayjs";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import styles from "../index.module.less";
import * as MessageType from "./message";

const MAX_WATCH_DOG = 60; // seconds
const makeTipMsg = (contactMessage) => {
  return {
    msgType: "tip",
    message: {
      text: dayjs(new Date(contactMessage.message.created_at * 1000)).format(
        "MM月DD日 HH:mm"
      ),
      _id_: contactMessage.message.created_at,
    },
  };
};

export default forwardRef(function MessageList(
  { onTopVisibleChange, onBottomVisibleChange },
  ref
) {
  const [messages, setMessages, getMessages] = useGetState([]);
  const bottom = useRef();
  const top = useRef();
  const container = useRef();

  const getPureMessages = useMemoizedFn(() =>
    getMessages().filter((m) => m.msgType !== "tip")
  );
  const getOldestMessage = useMemoizedFn(
    () => getMessages().filter((m) => m.msgType !== "tip")[0]
  );
  const getNewestMessage = useMemoizedFn(() => {
    const pureMessages = getPureMessages();
    return pureMessages.pop();
  });

  const watchDog = useRef({ counter: 0, taskId: null });
  const _ = useRef({ stopLoad: false });

  const checkWatchDog = useMemoizedFn(() => {
    if (watchDog.current.counter <= 0) {
      watchDog.current.taskId = null;
      return;
    }
    watchDog.current.counter -= 1;
    watchDog.current.taskId = setTimeout(checkWatchDog, 1000);
  });
  const feedDog = useMemoizedFn(() => {
    if (watchDog.current.taskId) {
      clearTimeout(watchDog.current.taskId);
    }
    watchDog.current.counter = MAX_WATCH_DOG;
    watchDog.current.taskId = setTimeout(checkWatchDog);
  });

  const toBottom = useMemoizedFn(() => {
    const list = container.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight - list.clientHeight;
  });

  const push = useMemoizedFn((msgType, elements) => {
    elements = !Array.isArray(elements) ? [elements] : elements;

    const messages = getMessages();
    const newMessages = elements.map((message) => ({ msgType, message }));
    const topMsg = messages[messages.length - 1];
    const nextToTopMsg = newMessages[0];
    const ts = (msg) => msg.message.created_at ?? msg.message.updated_at;
    const needInsertTS = !topMsg
      ? false
      : ["tip", "system"].includes(nextToTopMsg.msgType)
      ? false
      : watchDog.current.counter <= 0 ||
        (topMsg.msgType !== "tip" &&
          ts(nextToTopMsg) - ts(topMsg) >= MAX_WATCH_DOG);

    setMessages(
      [
        ...messages,
        needInsertTS && makeTipMsg(nextToTopMsg),
        ...newMessages,
      ].filter((r) => r)
    );
    if (topMsg?.msgType !== "system") requestAnimationFrame(toBottom);
  });
  const append = useMemoizedFn((msgType, elements) => {
    elements = !Array.isArray(elements) ? [elements] : elements;

    const t = [
      ...elements.map((message) => ({ msgType, message })),
      ...getMessages(),
    ];
    if (t[0].msgType === "contact") {
      // 添加时间节点
      t.unshift(makeTipMsg(t[0]));
    }
    setMessages(t);

    if (msgType !== "system") {
      // 保持当前相对位置不变
      container.current.scrollTo({ top: elements.length * 50 });
    }
  });
  const clear = useMemoizedFn((msgType, elements) => {
    if (!msgType) {
      setMessages([]);
      return;
    }
    elements = !Array.isArray(elements) ? [elements] : elements;
    const t = elements.map((message) => ({ msgType, message }));
    if (t.length && t[0].msgType === "contact") {
      setMessages([makeTipMsg(t[0]), ...t]);
    } else {
      setMessages(t);
    }

    if (msgType !== "system") {
      requestAnimationFrame(toBottom);
    }
  });

  useImperativeHandle(
    ref,
    () => ({
      push,
      append,
      clear,
      getOldestMessage,
      getNewestMessage,
      feedDog,
      getPureMessages,
    }),
    [
      getPureMessages,
      feedDog,
      push,
      append,
      clear,
      getOldestMessage,
      getNewestMessage,
    ]
  );

  useEffect(() => {
    watchDog.current.taskId = setTimeout(checkWatchDog);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (_.current.stopLoad) {
              return;
            } else {
              _.current.stopLoad = true;
            }
          } else {
            _.current.stopLoad = false;
          }

          const callback =
            entry.target === top.current
              ? onTopVisibleChange
              : onBottomVisibleChange;
          callback && callback(entry.isIntersecting);
        }
      },
      {
        threshold: 0.5,
      }
    );
    if (messages.length) {
      observer.observe(top.current);
      observer.observe(bottom.current);
    }

    const r = watchDog.current;

    return () => {
      observer.disconnect();
      clearTimeout(r.taskId);
    };
  }, [checkWatchDog, messages, onBottomVisibleChange, onTopVisibleChange]);

  return (
    <div
      className={classNames(styles.messages, "custom-scroll")}
      ref={container}
    >
      {messages.length ? (
        <>
          <div ref={top} key={"top"} />
          {messages.map(({ msgType, message }, idx) => {
            const Message = Reflect.get(MessageType, msgType);
            const defaultProps = {};
            // 默认展开系统消息第一条
            if (idx == 0 && msgType === "system") {
              defaultProps.defaultFolded = false;
            }
            return (
              <Message
                message={message}
                key={`${msgType}_${message._id_}`}
                {...defaultProps}
              />
            );
          })}
          <div ref={bottom} key={"bottom"} />
        </>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "rgba(30, 32, 35, 0.48)",
            fontSize: "16px",
            fontWeight: "600",
            height: "100%",
            padding: 0,
            margin: 0,
          }}
        >
          还没有私信哦～
        </div>
      )}
    </div>
  );
});
